import { useMemo } from 'react'

import {
  getSelectedOptionsForProductCard,
  getVariantTags,
  navigateToPDP,
} from '@src/components/product-card/product-card.helpers'
import { useRefreshedTags } from '@src/components/product-card/product-card.hooks'
import { IWebsiteProduct, ProductCardTag } from '@src/components/product-card/product-card.types'
import { useQuinceRouter as useRouter } from '@src/modules/navigation'
import {
  getIndividualTagStatus,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import productBuyNowStateManager from '@src/quince-site-common/src/redux/globalStateManager/ProductBuyNowStateManager'
import { useProductDynamicData } from '@src/quince-site-common/src/redux/globalStateManager/productDynamicDataStateManager'
import getContentContainerDetails from '@src/quince-site-common/src/utils/getContentContainerDetails'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

const useButtonClickWithTracking = (
  product?: IWebsiteProduct,
  href?: string,
  trackingMetaData?: TrackingMetaData,
  tracker?: string,
  productPosition?: number,
) => {
  const { deviceType } = useDeviceInfo()
  const router = useRouter()
  const selectedVariant = product?.cardVariants[0]

  const { productTags: refreshedProductTags } = useProductDynamicData({
    productId: product?.productId ?? '',
    isProductConstructedOnServer: true,
    skipTagRefresh: false,
  })

  const refreshedTagsForSelectedVariant = useRefreshedTags(
    product?.cardVariants || [],
    selectedVariant,
    refreshedProductTags,
  )

  const { applicableTags }: { applicableTags: ProductCardTag[] } = useMemo(
    () => getVariantTags(selectedVariant, refreshedTagsForSelectedVariant),
    [selectedVariant, refreshedTagsForSelectedVariant],
  )

  const onButtonClick = () => {
    if (product) {
      const contentContainerDetails = getContentContainerDetails(trackingMetaData)

      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
        context: {
          ...trackingMetaData?.context,
          contentContainerDisplayName: contentContainerDetails.contentContainerDisplayName,
          contentContainerType: contentContainerDetails.contentContainerType,
          contentContainerName: contentContainerDetails.contentContainerName,
          productPosition,
          tags: applicableTags,
          tag_ids: transformTagsIntoAnalyticsTags(applicableTags.map(tag => tag.value)),
          ...getIndividualTagStatus(applicableTags.map(tag => tag.value as ProductTagKey)),
        },
      })
      if (deviceType === DEVICE_TYPES.MOBILE) {
        const selectedColor = product?.cardVariants[0]?.displayConfig?.value
        navigateToPDP({ slug: product?.slug, tracker, selectedOption: selectedColor, router })
      } else {
        productBuyNowStateManager.showBuyNowPopUp({
          productCard: { product },
          selectedOptions: getSelectedOptionsForProductCard(product.cardVariants[0]),
        })
      }
    } else if (href) {
      router.push(href)
    }
  }

  return onButtonClick
}

export default useButtonClickWithTracking
